// #region Imports
import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import { ARCHIVED_TEMPLATES_PATH } from "work/fee-schedule-templates/_views/pages/fee-schedule-list-chips";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { alpha, styled, useTheme, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { refreshStaleStateOnce } from "_shared/_errors/stale-state";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import TagInput from "_shared/_views/tags/tag-input";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { withFeeScheduleTeamMember } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import WhoHasAccessButton from "permissions/_views/records/who-has-access-button";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import useConfirmLeavingUnsavedEdits from "work/_views/use-confirm-leaving-unsaved-edits";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import {
  useArchiveFeeScheduleTemplateMutation,
  useCreateFeeScheduleCategoryMutation,
  useCreateFeeScheduleTemplateMutation,
  useDeleteFeeScheduleTemplateMutation,
  useGetFeeScheduleTemplateByIdQuery,
  useUnarchiveFeeScheduleTemplateMutation,
  useUpdateFeeScheduleCategoryMutation,
  useUpdateFeeScheduleTemplateMutation,
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import {
  getFeeScheduleTemplateById,
  isFeeScheduleTemplateLoadIssue,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleTemplatesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import type { TemplateCategoriesApplied } from "work/fee-schedule-templates/_views/lists/fee-schedule-template-category-picker";
import EditFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/edit-fee-schedule-category-drawer";
import ExistingFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-category-drawer";
import ExistingFeeScheduleTemplateDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-template-drawer";
import { startDraftProposalFromFeeScheduleTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  readFeeScheduleTemplateFailure,
  type FeeScheduleTemplateFailure,
} from "work/fee-schedule-templates/_api/fee-schedule-template-error";
// #endregion

function getCategorySaveErrorMessage(error: unknown, action: "create" | "update"): string {
  const failure = readFeeScheduleTemplateFailure(error);
  return failure.message ?? `Unable to ${action} fee schedule category. Check the entered values and try again.`;
}

function isTemplateConflict(failure: FeeScheduleTemplateFailure): boolean {
  return failure.apiError.status === HttpStatusCode.Conflict
    || failure.apiError.status === HttpStatusCode.PreconditionFailed;
}

async function refreshTemplateAfterConflict(
  error: unknown,
  failure: FeeScheduleTemplateFailure,
  commandKey: string,
  refetch: () => Promise<void>,
): Promise<void> {
  if (!isTemplateConflict(failure)) return;
  try {
    await refreshStaleStateOnce(error, commandKey, refetch);
  } catch {
    // Keep the conflict visible. A failed refresh must never replay the write.
  }
}

function getTemplateSaveErrorMessage(failure: FeeScheduleTemplateFailure, isLocalError: boolean): string {
  if (failure.apiError.status === HttpStatusCode.BadRequest && !failure.apiError.traceId && !failure.apiError.trusted)
    return "Unable to save template at this time, please check all fields and try again.";
  if (failure.message) return failure.message;
  if (isLocalError) return "Error saving fee schedule template";
  if (failure.apiError.status === HttpStatusCode.BadRequest)
    return "Unable to save template at this time, please check all fields and try again.";
  return "Unable to save this fee template. Check the highlighted fields and try again.";
}

// #region Styled Components
/**
 * The inset on either side of a phone's page — one number for the header, the Name and Tags fields and
 * the table's toolbars, so they all stand the same distance off both edges and only the table's rows
 * run to them.
 *
 * A viewport query rather than a pane's container query: the drawers here cover the page rather than
 * docking beside it, and below Material's `lg` the navigation drawer is temporary, so on a phone the
 * window is the page.
 */
const PHONE_INSET_UNITS = 2;

function onAPhone(theme: Theme): string {
  return theme.breakpoints.down("sm");
}

const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  paddingBottom: 0,
  // The title keeps one line, and the actions take the line beneath it once they no longer fit beside
  // it — Delete, Archive and Save between them are wider than a phone's title row has left, and with
  // Who has access (#6839) an edit page's five are wider than a laptop's has.
  columnGap: theme.spacing(2),
  flexWrap: "wrap",
  rowGap: theme.spacing(1),
  [onAPhone(theme)]: {
    columnGap: theme.spacing(1),
    padding: theme.spacing(2, PHONE_INSET_UNITS, 0),
  },
}));

const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "row",
  minWidth: 0,
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [onAPhone(theme)]: {
    fontSize: "1.25rem",
  },
}));

export const Header = styled("section")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  position: "sticky",
  top: "0px",
  zIndex: 10,
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  // A button that runs out of room moves to the next line whole, rather than its label taking two.
  "& .MuiButton-root": { whiteSpace: "nowrap" },
  [onAPhone(theme)]: {
    gap: theme.spacing(1),
    // A large button's 22px of padding a side is what put a create page's Delete, Archive and Save on
    // two lines; at 12px the three share one under the title.
    "& .MuiButton-root": { paddingInline: theme.spacing(1.5) },
    "& .MuiButton-startIcon": { marginRight: theme.spacing(0.5) },
  },
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "dimmed",
})<{
  dimmed?: boolean;
}>(({ dimmed, theme }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  // No padding of the page's own on a phone: each row below carries the inset itself, so that the
  // table can run to the edges while everything else stands in from them.
  [onAPhone(theme)]: {
    padding: 0,
  },
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  marginRight: 0,
  position: "relative",
  width: "100%",
  "&::after": {
    backgroundColor: alpha(theme.palette.background.default, 0.38),
    content: '""',
    inset: 0,
    opacity: dimmed ? 1 : 0,
    pointerEvents: "none",
    position: "absolute",
    transition: theme.transitions.create("opacity", {
      duration: dimmed ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      easing: dimmed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    }),
    zIndex: theme.zIndex.drawer - 1,
  },
}));

export const Content = styled("section")(({ theme }) => ({
  alignItems: "center",
  flex: 1,
  flexDirection: "row",
  height: "100%",
  marginTop: theme.spacing(2),
  minHeight: "25rem",
  overflow: "auto",
  width: "100%",
}));

export const PageContent = styled("section")(() => ({
  alignItems: "stretch",
  flexDirection: "column",
  justifyContent: "center",
  display: "flex",
  flex: 1,
  height: "100%",
  overflow: "visible",
  width: "100%",
}));

const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const DetailsContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(5),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  padding: theme.spacing(0, 3),
  width: "100%",
  "& > .MuiTextField-root": {
    width: "50%",
  },
  // Side by side, a phone left the Name field 85px and wrapped its helper text over three lines.
  [onAPhone(theme)]: {
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(0, PHONE_INSET_UNITS),
    "& > *, & > .MuiTextField-root": {
      width: "100%",
    },
  },
}));

const TemplatesTable = styled(FeeScheduleTemplatesTable)(({ theme }) => ({
  height: "100%",
  // The table's rows run edge to edge, and everything else in it — the banner, the toolbar and the
  // pager — takes the page's inset back, so the search lines up with the title, fields and warning
  // above it.
  "& > .MuiAlert-root, & > .MuiPaper-root > :not(.MuiTableContainer-root)": {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    [onAPhone(theme)]: {
      marginLeft: theme.spacing(PHONE_INSET_UNITS),
      marginRight: theme.spacing(PHONE_INSET_UNITS),
    },
  },
  // The margin above is the whole inset. MRT pads the top toolbar's row by 8px only while the pane is
  // wide, which would leave the search 8px inside the fields on a desktop and flush with them on a
  // narrow pane.
  "& > .MuiPaper-root > :first-child > .MuiBox-root": {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));
// #endregion

type FeeScheduleTemplateAction = "edit" | "create" | "view";
const FeeScheduleTemplateActions = new Set<FeeScheduleTemplateAction>(["edit", "create", "view"]);
const CATEGORY_ALERT_BANNER_TIMEOUT_MS = 5000;
const DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS = 250;

function canonicalCategoryName(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name.value.trim().toLowerCase();
}

function splitCategoriesByCanonicalName(
  existingCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  incomingCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
) {
  const names = new Set(existingCategories.map(canonicalCategoryName));
  const duplicateCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> = [];
  const uniqueCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> = [];
  for (const category of incomingCategories) {
    const name = canonicalCategoryName(category);
    if (names.has(name)) duplicateCategories.push(category);
    else {
      names.add(name);
      uniqueCategories.push(category);
    }
  }
  return { duplicateCategories, uniqueCategories };
}

/**
 * The catalog's answer for a category, with the person the author named for it.
 *
 * A catalog row names nobody, because every template in the firm shares it, so it is sent and comes
 * back without the person (#6091). Who this template's rate is for lives on the template, and the
 * template save writes it, so the page keeps it on its own copy in the meantime.
 */
function withTemplateStaffing<T extends FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>(
  saved: T,
  written: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
): T {
  return written.teamMember ? withFeeScheduleTeamMember(saved, written.teamMember) : saved;
}

export default function FeeScheduleTemplatePage() {
  // #region Refs
  const nameFieldRef = useRef<HTMLInputElement>(null);
  const hydratedTemplateIdRef = useRef<string | null>(null);
  const commandInFlightRef = useRef(false);

  // #region Hooks
  // The same viewport question `onAPhone` asks of the styles, for the props a style cannot set.
  const isPhone = useMediaQuery(useTheme().breakpoints.down("sm"));
  const location = useLocation();
  const session = useSession();
  const [newCategoryFormKey, setNewCategoryFormKey] = useState(0);
  const { action: actionParam, id: idParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const feeScheduleTemplateAccess = useWhoHasAccess("fee-schedule-templates", "the fee schedule template");

  // #region Route Info
  const feeScheduleTemplateId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const pageAction: FeeScheduleTemplateAction = (actionParam as FeeScheduleTemplateAction) ?? "create";
  const isArchivedTemplateRoute = location.pathname.startsWith("/fee-schedules/archived/");

  // Unsupported action
  if (!FeeScheduleTemplateActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }
  // Fee schedule template id is required for edit/view
  if (pageAction !== "create" && !idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Fee Schedule Template Queries
  const cachedFeeScheduleTemplateJson = useSelector(getFeeScheduleTemplateById(feeScheduleTemplateId));
  const feeScheduleTemplateQuery = useGetFeeScheduleTemplateByIdQuery(
    !feeScheduleTemplateId || feeScheduleTemplateId.isEmpty
      ? skipToken
      : { feeScheduleTemplateId: feeScheduleTemplateId.value },
  );
  const feeScheduleTemplateJson = feeScheduleTemplateQuery.data ?? cachedFeeScheduleTemplateJson;
  const feeScheduleTemplate = useMemo(() => {
    if (!feeScheduleTemplateJson || isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson) || !session.user)
      return null;
    return FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
  }, [feeScheduleTemplateJson, session.user]);

  // #region State Variables
  const [isAddFeePanelOpen, setIsAddFeePanelOpen] = useState<boolean>(false);
  const [isExistingFeePanelOpen, setIsExistingFeePanelOpen] = useState<boolean>(false);
  const [isExistingTemplatePanelOpen, setIsExistingTemplatePanelOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Every move this page makes is one it has already asked about or one a save, delete or archive
  // finished, so it navigates through the guard's pass rather than prompting again.
  const navigate = useConfirmLeavingUnsavedEdits(isFormDirty);
  const [needsFreshChoice, setNeedsFreshChoice] = useState(false);
  const [categoryAlertBannerMessage, setCategoryAlertBannerMessage] = useState<string | undefined>(undefined);
  const [templateErrorMessage, setTemplateErrorMessage] = useState<string | undefined>(undefined);
  const [templateFieldErrors, setTemplateFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [categoryFieldErrors, setCategoryFieldErrors] = useState<Readonly<Record<string, string>>>({});

  const [name, setName] = useState<string>(feeScheduleTemplate?.name ?? "");
  const [tags, setTags] = useState<FeeScheduleTag[]>([]);
  const [categories, setCategories] = useState<Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>>(
    feeScheduleTemplate?.categories ?? [],
  );
  const [currentCategory, setCurrentCategory] = useState<
    FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined
  >(undefined);
  // #endregion

  // #region Form State
  const isLoading = feeScheduleTemplateQuery.isLoading || sessionPermissionsLoading;
  const disableEditing = pageAction === "view" || !pageAction;
  const isTemplateArchived = isArchivedTemplateRoute || Boolean(feeScheduleTemplate?.archived);
  // Who bills a rate is optional here, hourly or not (#6605): it depends on the team a proposal
  // assembles, and the proposal asks for it when it is approved or hired.
  const isFormValid = Boolean(name) && categories.length > 0;
  const disableSave = isLoading || disableEditing || !isFormDirty || !isFormValid || needsFreshChoice;
  const showArchiveButton = !disableEditing || isTemplateArchived;
  const isAnyDrawerOpen = isAddFeePanelOpen || isExistingFeePanelOpen || isExistingTemplatePanelOpen;
  const isSaveButtonDisabled = isArchiving || isSaving || disableSave;

  /**
   * Stable on purpose, and it has to be.
   *
   * `DebouncedTextField` rebuilds its debounced push whenever this callback's identity changes, and
   * its cleanup cancels the pending one — so a fresh arrow on every render means any re-render
   * inside the 300ms window throws away what was typed. The field keeps showing the text, because
   * it holds its own local value, so the loss is invisible: the name never reaches this page, Save
   * stays disabled, and the reason it gives is "Add a name and category" for a form that plainly
   * has a name.
   *
   * This page is in `scripts/react-compiler-baseline.txt`, so nothing memoizes it for us. See
   * app/CLAUDE.md: never let correctness depend on the compiler.
   */
  const handleNameChanged = useCallback((newValue: string) => {
    setName(newValue);
    setNeedsFreshChoice(false);
    setIsFormDirty(true);
    setTemplateFieldErrors((current) => {
      const next = { ...current };
      delete next.name;
      return next;
    });
  }, []);

  const saveButtonTooltip = (): string => {
    if (!isSaveButtonDisabled) return "";
    if (isSaving) return "Template is saving.";
    if (isArchiving) return "Archive action in progress.";
    if (isLoading) return "Template is loading.";
    if (!name && categories.length === 0) return "Add a name and category.";
    if (!name) return "Add a template name.";
    if (categories.length === 0) return "Add at least one category.";
    if (needsFreshChoice) return "Change a field before saving again.";
    return "Make a change to save.";
  };

  const [createFeeScheduleTemplateQuery] = useCreateFeeScheduleTemplateMutation();
  const [createFeeScheduleCategoryQuery] = useCreateFeeScheduleCategoryMutation();
  const [updateFeeScheduleTemplateQuery] = useUpdateFeeScheduleTemplateMutation();
  const [updateFeeScheduleCategoryQuery] = useUpdateFeeScheduleCategoryMutation();
  const [deleteFeeScheduleTemplateQuery] = useDeleteFeeScheduleTemplateMutation();
  const [archiveFeeScheduleTemplateQuery] = useArchiveFeeScheduleTemplateMutation();
  const [unarchiveFeeScheduleTemplateQuery] = useUnarchiveFeeScheduleTemplateMutation();

  // #region Lifecycle
  useEffect(() => {
    // Fee schedule template invalid/not found
    const shouldShowNotFound =
      Boolean(feeScheduleTemplateId) &&
      !feeScheduleTemplateId?.isEmpty &&
      feeScheduleTemplate === null &&
      pageAction !== "create" &&
      !isLoading;

    if (shouldShowNotFound) {
      enqueueSnackbar("Fee schedule template not found", { variant: "warning" });
      navigate("/fee-schedules/templates", { replace: true });
    }
  }, [pageAction, navigate, feeScheduleTemplate, feeScheduleTemplateId, isLoading]);

  useEffect(() => {
    if (pageAction === "create") return;
    if (!feeScheduleTemplate?.id) return;
    if (isFormDirty) return;

    const templateId = feeScheduleTemplate.id.value;
    if (hydratedTemplateIdRef.current === templateId) return;

    setName(feeScheduleTemplate.name ?? "");
    setTags(feeScheduleTemplate.tags ?? []);
    setCategories(feeScheduleTemplate.categories ?? []);

    hydratedTemplateIdRef.current = templateId;
  }, [pageAction, feeScheduleTemplate, isFormDirty]);

  useEffect(() => {
    if (!categoryAlertBannerMessage) return;

    const timeoutId = globalThis.setTimeout(() => {
      setCategoryAlertBannerMessage(undefined);
    }, CATEGORY_ALERT_BANNER_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [categoryAlertBannerMessage]);

  useEffect(() => {
    globalThis.dispatchEvent(new Event("resize"));

    const timeoutId = globalThis.setTimeout(() => {
      globalThis.dispatchEvent(new Event("resize"));
    }, DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [isAnyDrawerOpen]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  // #region Functions
  function getIsCategoryAlreadySelected(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): boolean {
    const categoryName = canonicalCategoryName(category);
    return categories.some((selectedCategory) => canonicalCategoryName(selectedCategory) === categoryName);
  }

  const buildTemplate = useCallback((): FeeScheduleTemplate => {
    if (!session.user?.id) {
      throw new Error("User ID is required to build fee schedule template");
    }
    if (!session.user.legalEntityId) {
      throw new Error("Legal Entity ID is required to build fee schedule template");
    }

    if (!feeScheduleTemplateId) {
      return new FeeScheduleTemplate({
        name,
        tags,
        categories,
        creator: new EntityMember({
          userId: session.user.id,
          legalEntityId: session.user.legalEntityId,
        }),
      });
    }

    return new FeeScheduleTemplate({
      id: feeScheduleTemplateId,
      name,
      tags,
      categories,
      creator: new EntityMember({
        userId: session.user.id,
        legalEntityId: session.user.legalEntityId,
      }),
    });
  }, [categories, feeScheduleTemplateId, name, session.user, tags]);

  const closeDrawers = useCallback(() => {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
  }, []);

  function handleCreateCategory() {
    // A form that is opened is a form that starts empty, and this counter is what says so. The key
    // read `Guid.generate().value`, which is a fresh key on every RENDER rather than on every
    // opening: any re-render of this page while the drawer was open remounted it and threw away
    // what the author had typed. Nothing re-rendered it before the team-member roster started
    // arriving asynchronously, which is how it went unnoticed. Same idiom as `newScheduleForm` in
    // `manage-team-fee-schedules.tsx`.
    setNewCategoryFormKey((key) => key + 1);
    setCurrentCategory(undefined);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsAddFeePanelOpen(true);
  }

  function handleEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    setCurrentCategory(category);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsAddFeePanelOpen(true);
  }

  function handleAddExistingCategory() {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsExistingFeePanelOpen(true);
  }

  function handleSelectExistingTemplate() {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(true);
  }

  function handleExistingCategoriesSelected(
    selectedCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  ) {
    const { duplicateCategories, uniqueCategories } = splitCategoriesByCanonicalName(
      categories,
      selectedCategories,
    );
    const nextCategories = [...categories, ...uniqueCategories];

    setCategories(nextCategories);
    setNeedsFreshChoice(false);

    if (duplicateCategories.length > 0) {
      setCategoryAlertBannerMessage(
        "One or more categories from the selected template were not added because categories with the same name already exist in the fee schedule",
      );
    } else {
      setCategoryAlertBannerMessage(undefined);
    }

    setCurrentCategory(undefined);
    setIsFormDirty(true);
    setIsExistingFeePanelOpen(false);
  }

  /**
   * Handles when a category is added
   * @param category The category to add
   */
  async function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (commandInFlightRef.current) return;
    if (getIsCategoryAlreadySelected(category)) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      enqueueSnackbar("Currency-masked categories cannot be created", { variant: "error" });
      return;
    }

    // Create-page categories are local values until the template is saved. This is the same
    // persistence boundary used when creating a template from a proposal and avoids POSTing a
    // catalog row that can be orphaned if the surrounding template is abandoned.
    if (pageAction === "create") {
      setCategoryAlertBannerMessage(undefined);
      setCategories((prevCategories) => [...prevCategories, category]);
      setNeedsFreshChoice(false);
      setCurrentCategory(undefined);
      setIsFormDirty(true);
      setIsAddFeePanelOpen(false);
      return;
    }

    try {
      commandInFlightRef.current = true;
      setCategoryFieldErrors({});
      const categoryJSON = await createFeeScheduleCategoryQuery({ category }).unwrap();
      const savedCategory = withTemplateStaffing(FeeScheduleCategory.fromJSON(categoryJSON), category);

      setCategoryAlertBannerMessage(undefined);
      setCategories((prevCategories) => [...prevCategories, savedCategory]);
      setNeedsFreshChoice(false);
      setCurrentCategory(undefined);
      setIsFormDirty(true);
      setIsAddFeePanelOpen(false);
    } catch (error) {
      const failure = readFeeScheduleTemplateFailure(error);
      if (error instanceof Error && !("status" in error)) console.error("Failed to create fee schedule category", error);
      setCategoryFieldErrors(failure.fieldErrors);
      enqueueSnackbar(failure.message ?? getCategorySaveErrorMessage(error, "create"), { variant: "error" });
    }
    commandInFlightRef.current = false;
  }

  /**
   * Handles when a category is removed
   * @param name The name of the category to remove
   */
  async function handleCategoryRemoved(name: FeeScheduleCategoryName) {
    const updatedCategories = categories.filter((category) => category.name?.isEqualTo(name) === false);
    setCategoryAlertBannerMessage(undefined);
    setCategories(updatedCategories);
    setNeedsFreshChoice(false);
    setIsFormDirty(true);
  }

  /**
   * Handles when a category is updated
   * @param originalName The original name of the category
   * @param updatedCategory The updated fee category
   */
  async function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    if (commandInFlightRef.current) return;
    if (!originalName) return;

    const isDuplicateCategory = categories.some(
      (category) =>
        canonicalCategoryName(category) !== originalName.value.trim().toLowerCase()
          && canonicalCategoryName(category) === canonicalCategoryName(updatedCategory),
    );
    if (isDuplicateCategory) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    if (updatedCategory instanceof FeeScheduleCategoryMaskedCurrency) {
      enqueueSnackbar("Currency-masked categories cannot be updated", { variant: "error" });
      return;
    }

    if (pageAction === "create") {
      setCategoryAlertBannerMessage(undefined);
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.name?.isEqualTo(originalName) ? updatedCategory : category,
        ),
      );
      setIsFormDirty(true);
      setCurrentCategory(undefined);
      setIsAddFeePanelOpen(false);
      return;
    }

    try {
      commandInFlightRef.current = true;
      setCategoryFieldErrors({});
      const categoryJSON = await updateFeeScheduleCategoryQuery({ category: updatedCategory }).unwrap();
      const savedCategory = withTemplateStaffing(FeeScheduleCategory.fromJSON(categoryJSON), updatedCategory);

      const updatedCategories = [...categories].filter((category) => !category.name?.isEqualTo(originalName));
      updatedCategories.push(savedCategory);

      setCategoryAlertBannerMessage(undefined);
      setCategories(updatedCategories);
      setIsFormDirty(true);
      setCurrentCategory(undefined);
      setIsAddFeePanelOpen(false);
    } catch (error) {
      const failure = readFeeScheduleTemplateFailure(error);
      setCategoryFieldErrors(failure.fieldErrors);
      enqueueSnackbar(failure.message ?? getCategorySaveErrorMessage(error, "update"), { variant: "error" });
    }
    commandInFlightRef.current = false;
  }

  /**
   * The categories the reader took from an existing template (#6604). Add or Replace is chosen in the
   * drawer's footer, where the reader can see what they are getting, rather than asked afterwards.
   */
  function handleExistingTemplateCategoriesApplied({ categories: chosen, mode }: TemplateCategoriesApplied) {
    if (mode === "replace" || categories.length === 0) {
      setCategoryAlertBannerMessage(undefined);
      setCategories(chosen);
      setIsFormDirty(true);
      setIsExistingTemplatePanelOpen(false);
    } else {
      const { duplicateCategories, uniqueCategories } = splitCategoriesByCanonicalName(
        categories,
        chosen,
      );
      setCategories((prevCategories) => [...prevCategories, ...uniqueCategories]);

      if (duplicateCategories.length > 0) {
        setCategoryAlertBannerMessage(
          "One or more categories from the selected template were not added because categories with the same name already exist in the fee schedule",
        );
      } else {
        setCategoryAlertBannerMessage(undefined);
      }

      setIsFormDirty(true);
      setIsExistingTemplatePanelOpen(false);
    }
  }

  async function handleDeleteClicked() {
    if (commandInFlightRef.current) return;
    if (!feeScheduleTemplate?.id) {
      console.warn("Fee schedule template not found");
      return;
    }

    try {
      const deleteResponse = await confirm({
        title: "Delete Fee Schedule Template?",
        message: "This cannot be undone.",
      });

      if (deleteResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      commandInFlightRef.current = true;
      await deleteFeeScheduleTemplateQuery({ templateId: feeScheduleTemplate.id.value }).unwrap();
      enqueueSnackbar("Fee schedule template deleted", { variant: "info" });
      closeDrawers();
      navigate("/fee-schedules/templates", { replace: true });
    } catch (error) {
      const failure = readFeeScheduleTemplateFailure(error);
      await refreshTemplateAfterConflict(error, failure, `fee-schedule-template.delete:${feeScheduleTemplate.id.value}`, async () => {
        if (typeof feeScheduleTemplateQuery.refetch === "function") await feeScheduleTemplateQuery.refetch();
      });
      if (error instanceof Error && !("status" in error)) console.error("Failed to delete fee schedule template", error);
      enqueueSnackbar(failure.message ?? "Failed to delete fee schedule template", { variant: "error" });
    } finally {
      commandInFlightRef.current = false;
      setIsDeleting(false);
    }
  }

  async function handleArchiveClicked() {
    if (commandInFlightRef.current) return;
    if (!feeScheduleTemplate?.id) {
      console.warn("Fee schedule template not found");
      return;
    }

    const shouldArchive = !isTemplateArchived;
    const action = shouldArchive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: `${action} Fee Schedule Template?`,
        message: shouldArchive
          ? "Archive this fee schedule template?"
          : "Unarchive this fee schedule template?",
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsArchiving(true);
      commandInFlightRef.current = true;
      if (shouldArchive) {
        await archiveFeeScheduleTemplateQuery({ templateId: feeScheduleTemplate.id.value }).unwrap();
        enqueueSnackbar("Fee schedule template archived", { variant: "success" });
        closeDrawers();
        navigate(ARCHIVED_TEMPLATES_PATH, { replace: true });
      } else {
        await unarchiveFeeScheduleTemplateQuery({ templateId: feeScheduleTemplate.id.value }).unwrap();
        enqueueSnackbar("Fee schedule template unarchived", { variant: "success" });
        closeDrawers();
        navigate("/fee-schedules/templates", { replace: true });
      }
    } catch (error) {
      const failure = readFeeScheduleTemplateFailure(error);
      await refreshTemplateAfterConflict(error, failure, `fee-schedule-template.${shouldArchive ? "archive" : "unarchive"}:${feeScheduleTemplate.id.value}`, async () => {
        if (typeof feeScheduleTemplateQuery.refetch === "function") await feeScheduleTemplateQuery.refetch();
      });
      if (error instanceof Error && !("status" in error)) console.error(`Failed to ${action.toLowerCase()} fee schedule template`, error);
      enqueueSnackbar(
        failure.message ?? `Failed to ${action.toLowerCase()} fee schedule template`,
        { variant: "error" },
      );
      setNeedsFreshChoice(false);
    } finally {
      commandInFlightRef.current = false;
      setIsArchiving(false);
    }
  }

  // The draft starts from the saved template, and it goes through this page's `navigate`, which the
  // guard does not question. So the edits on screen would be dropped without a word: ask here first
  // (review of #7070).
  const handleCreateProposalClicked = useCallback(async () => {
    if (!feeScheduleTemplate) return;
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "The proposal starts from the saved template. Creating it discards the changes you have not saved.",
        okButtonText: "Discard",
        cancelButtonText: "Cancel",
      });
      if (response !== ConfirmResponse.Ok) return;
    }
    startDraftProposalFromFeeScheduleTemplate({
      dispatch,
      navigate,
      session,
      template: feeScheduleTemplate,
    });
  }, [confirm, dispatch, feeScheduleTemplate, isFormDirty, navigate, session]);

  const handleSaveTemplate = useCallback(async () => {
    if (commandInFlightRef.current) return;
    commandInFlightRef.current = true;
    setIsSaving(true);
    setTemplateErrorMessage(undefined);
    setTemplateFieldErrors({});
    try {
      let responseJSON: FeeScheduleTemplateJSON;

      if (feeScheduleTemplateId && feeScheduleTemplate) {
        responseJSON = await updateFeeScheduleTemplateQuery({
          templateId: feeScheduleTemplateId.value,
          originalTemplate: feeScheduleTemplate,
          updatedTemplate: buildTemplate(),
        }).unwrap();
      } else {
        responseJSON = await createFeeScheduleTemplateQuery({
          template: buildTemplate(),
          // Categories created on this page remain local values until this request. The server
          // validates and promotes them atomically with the template, matching proposal creation.
          categorySource: "values",
        }).unwrap();
      }

      if (!responseJSON.id) throw new Error("Saved fee schedule template is missing an ID");

      setIsFormDirty(false);
      enqueueSnackbar(`${feeScheduleTemplateId ? "Template updated" : "Template created"} successfully`, {
        variant: "success",
      });

      if (responseJSON.id && !location.pathname.includes("/fee-schedules/templates/edit")) {
        closeDrawers();
        navigate(`/fee-schedules/templates/edit/${responseJSON.id}`, { replace: true });
      }
    } catch (error) {
      const failure = readFeeScheduleTemplateFailure(error);
      setTemplateFieldErrors(failure.fieldErrors);
      if (feeScheduleTemplateId && isTemplateConflict(failure)) {
        setNeedsFreshChoice(true);
        await refreshTemplateAfterConflict(error, failure, `fee-schedule-template.patch:${feeScheduleTemplateId.value}`, async () => {
            if (typeof feeScheduleTemplateQuery.refetch === "function") await feeScheduleTemplateQuery.refetch();
        });
      }
      const isLocalError = error instanceof Error && !("status" in error);
      if (isLocalError) console.error("Error saving fee schedule template", error);
      const message = getTemplateSaveErrorMessage(failure, isLocalError);
      setTemplateErrorMessage(message);
      enqueueSnackbar(message, {
        variant: failure.apiError.source === "legacy" || failure.apiError.status === HttpStatusCode.BadRequest ? "warning" : "error",
      });
    } finally {
      commandInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [
    feeScheduleTemplateId,
    feeScheduleTemplate,
    location.pathname,
    updateFeeScheduleTemplateQuery,
    buildTemplate,
    createFeeScheduleTemplateQuery,
    feeScheduleTemplateQuery,
    closeDrawers,
    navigate,
  ]);

  // #endregion

  // #region Render
  const pageTitle = useMemo(() => {
    if (pageAction === "create") return "Create";
    if (pageAction === "edit") return "Edit Template";
    return "View";
  }, [pageAction]);

  const handleNavigateBack = useCallback(async () => {
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
      if (response === ConfirmResponse.AltOk) {
        await handleSaveTemplate();
      }
    }

    closeDrawers();
    navigate("/fee-schedules/templates", { replace: true });
  }, [closeDrawers, confirm, disableSave, handleSaveTemplate, isFormDirty, navigate]);

  if (!sessionPermissions) {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return (
    <PageContainer>
      <Main dimmed={isAnyDrawerOpen}>
        <HeaderContainer>
          <TitleContainer>
            <PageTitle variant="h4" aria-label={breadcrumbHeadingName(["Fee Schedules"], pageTitle)}>
              {/*
                * The way back is the breadcrumb, not a "Back" chip beside it (#5907). It still offers to
                * save or discard an edit first, which is why it hands the click to `handleNavigateBack`
                * rather than following the link itself.
                */}
              <PageTitleBreadcrumb
                label="Fee Schedules"
                to="/fee-schedules/templates"
                replace
                onFollow={handleNavigateBack}
              />
              {pageTitle}
            </PageTitle>
          </TitleContainer>
          <ActionButtonsContainer>
            {feeScheduleTemplateAccess.drawer}
            {/* Reading who holds a grant changes nothing, so it comes before the actions that do. */}
            {feeScheduleTemplateAccess.mayOpen && pageAction !== "create" && feeScheduleTemplate?.id && (
              <WhoHasAccessButton
                iconOnly
                size={isPhone ? "medium" : "large"}
                onClick={() => {
                  if (feeScheduleTemplate?.id) feeScheduleTemplateAccess.open(feeScheduleTemplate.id);
                }}
              />
            )}
            {pageAction === "edit" && !isTemplateArchived && (
              <LoadingButton
                variant="outlined"
                size={isPhone ? "medium" : "large"}
                color="primary"
                loading={false}
                disabled={isLoading || isDeleting || isArchiving || isSaving || !feeScheduleTemplate}
                startIcon={<WorkIcon />}
                onClick={handleCreateProposalClicked}
              >
                Create Proposal
              </LoadingButton>
            )}
            {!disableEditing && (
              <LoadingButton
                variant="outlined"
                size={isPhone ? "medium" : "large"}
                loading={isDeleting}
                color="error"
                disabled={isLoading || isDeleting || isArchiving || isSaving || !feeScheduleTemplate}
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClicked}
              >
                Delete
              </LoadingButton>
            )}
            {showArchiveButton && (
              <LoadingButton
                variant="outlined"
                size={isPhone ? "medium" : "large"}
                loading={isArchiving}
                color="inherit"
                disabled={isLoading || isDeleting || isArchiving || isSaving || !feeScheduleTemplate}
                startIcon={isTemplateArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={handleArchiveClicked}
              >
                {isTemplateArchived ? "Unarchive" : "Archive"}
              </LoadingButton>
            )}
            {!disableEditing && (
              <Tooltip title={saveButtonTooltip()} placement="bottom">
                <span>
                  <LoadingButton
                    variant="contained"
                    size={isPhone ? "medium" : "large"}
                    loading={isSaving}
                    color="primary"
                    disabled={isSaveButtonDisabled}
                    startIcon={<SaveIcon />}
                    onClick={handleSaveTemplate}
                  >
                    Save
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
          </ActionButtonsContainer>
        </HeaderContainer>
        <Content>
          <PageContent>
            {!disableEditing && (
              <DetailsContainer>
                <DebouncedTextField
                  inputRef={nameFieldRef}
                  required
                  label="Name"
                  error={Boolean(templateFieldErrors.name)}
                  // On a phone the hint repeats the label and costs a line; an error still shows.
                  helperText={templateFieldErrors.name ?? (isPhone ? undefined : "The template's name")}
                  size={isPhone ? "small" : "medium"}
                  slotProps={{
                    htmlInput: { maxLength: 50 },
                  }}
                  value={name}
                  onDebouncedChange={handleNameChanged}
                />
                <TagInput
                  helperText={isPhone ? undefined : `Tags to help you find this template later (ex. US, ${new AHDate().year})`}
                  placeholder={isPhone ? `Add a tag (ex. US, ${new AHDate().year})` : undefined}
                  size={isPhone ? "small" : "medium"}
                  onTagsUpdated={(tagValues) => {
                    setTags(tagValues.map((tagValue) => new FeeScheduleTag(tagValue)));
                    setNeedsFreshChoice(false);
                    setIsFormDirty(true);
                  }}
                  onTagRemoved={(tagValue) => {
                    setTags((prevTags) => prevTags.filter((prevTag) => prevTag.value !== tagValue));
                    setNeedsFreshChoice(false);
                    setIsFormDirty(true);
                  }}
                  tags={tags.map((tag) => tag.value)}
                />
              </DetailsContainer>
            )}
            {templateErrorMessage && (
              <Typography color="error" sx={{ px: { xs: PHONE_INSET_UNITS, sm: 3 } }} role="alert">
                {templateErrorMessage}
              </Typography>
            )}
            <TemplatesTable
              selectedCategories={categories}
              disableEditing={disableEditing}
              disableCategorySelection={disableEditing}
              hideActionsColumn={disableEditing}
              hideFieldHistory={true}
              alertBannerMessage={categoryAlertBannerMessage}
              onCreateCategoryClicked={handleCreateCategory}
              onExistingCategoryClicked={handleAddExistingCategory}
              onExistingTemplateClicked={handleSelectExistingTemplate}
              onEditCategory={handleEditCategory}
              onCategoryRemoved={handleCategoryRemoved}
            />
          </PageContent>
        </Content>
      </Main>
      <div style={{ width: isAnyDrawerOpen ? SIDE_PANEL_WIDTH : 0 }}>
        {/*
          Who a template's rate is for is chosen through the panel's picker, and nothing else (#5393).

          A template is not a vendor thing or a client thing. It belongs to the legal entity, carries
          no role, and the same template can be ticked from either side of a proposal — the two sides
          differ only in which list of applied template ids the proposal records it under. So the field
          is drawn for whoever is editing, and the picker's tabs reach the firm's own people and
          everybody else.

          Where the assignment finally has to hold is the proposal, not here.
          `WorkAgreement.ValidateFeeSchedule` checks an hourly rate's person against that proposal's
          vendor team, at hire time. A template cannot know which proposal it will be applied to, so
          the apply paths put anyone the proposal's team does not have to the user, and they ask a
          client the same question they ask a vendor. See `useReconcileFeeScheduleStaffing`.
        */}
        <EditFeeScheduleCategoryDrawer
          key={currentCategory?.id.value ?? `new-category-${newCategoryFormKey}`}
          category={currentCategory}
          isOpen={isAddFeePanelOpen}
          onOpen={() => setIsAddFeePanelOpen(true)}
          onClose={() => {
            setIsAddFeePanelOpen(false);
            setCurrentCategory(undefined);
          }}
          onCategoryAdded={handleCategoryAdded}
          onCategoryUpdated={handleCategoryUpdated}
          fieldErrors={categoryFieldErrors}
          offersTeamMember
          teamMemberOnEveryFee
        />
        <ExistingFeeScheduleCategoryDrawer
          selectedCategories={categories}
          isOpen={isExistingFeePanelOpen}
          onOpen={() => setIsExistingFeePanelOpen(true)}
          onClose={() => setIsExistingFeePanelOpen(false)}
          onCategoriesSelected={handleExistingCategoriesSelected}
        />
        <ExistingFeeScheduleTemplateDrawer
          isOpen={isExistingTemplatePanelOpen}
          onOpen={() => setIsExistingTemplatePanelOpen(true)}
          onClose={() => setIsExistingTemplatePanelOpen(false)}
          existingCategories={categories}
          onCategoriesApplied={handleExistingTemplateCategoriesApplied}
        />
      </div>
    </PageContainer>
  );
}
